import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Bundle,
  BundleSubscription,
  BundlePriceMap,
  BundleItem,
  SupportedCurrency
} from '@/types/bundle';
import { getReferralCookie } from './referralTrackingService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}` : 'http://localhost:5001/api';

const BUNDLES_COLLECTION = 'bundles';
const BUNDLE_SUBSCRIPTIONS_COLLECTION = 'bundleSubscriptions';

const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['usd', 'cad', 'gbp'];

const convertTimestamp = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (typeof numeric !== 'number' || Number.isNaN(numeric)) {
    return null;
  }
  return numeric;
};

const normalizeDisplayOrder = (value: unknown): number | null => {
  const numeric = toNumberOrNull(value);
  if (numeric == null) return null;
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric) || numeric < 1) {
    return null;
  }
  return numeric;
};

const normalizePrices = (prices: DocumentData | undefined): BundlePriceMap => {
  const result: BundlePriceMap = {
    usd: { oneTime: null, subscription: null },
    cad: { oneTime: null, subscription: null },
    gbp: { oneTime: null, subscription: null }
  };

  SUPPORTED_CURRENCIES.forEach((currency) => {
    const currencyPrice = prices?.[currency] || {};
    result[currency] = {
      oneTime: toNumberOrNull(currencyPrice.oneTime),
      subscription: toNumberOrNull(currencyPrice.subscription)
    };
  });

  return result;
};

const normalizeItems = (items: DocumentData | undefined): BundleItem[] => {
  if (!Array.isArray(items)) return [];
  return items.map((item: any) => ({
    productId: item.productId,
    quantity: item.quantity || 1,
    productName: item.productName,
    image: item.image,
    listingQuantity: item.listingQuantity,
    isCustom: item.isCustom ?? false,
    customSku: item.customSku ?? null,
    customDescription: item.customDescription ?? null
    ,
    brand: item.brand ?? null,
    netWeight: typeof item.netWeight === 'number' ? item.netWeight : item.netWeight ? Number(item.netWeight) : null,
    weightUnit: item.weightUnit || null
  }));
};

const mapBundleDoc = (docSnap: any): Bundle => {
  const data = docSnap.data ? docSnap.data() : {};

  return {
    id: docSnap.id,
    name: data.name || '',
    slug: data.slug || docSnap.id,
    description: data.description || '',
    images: data.images || [],
    heroImage: data.heroImage || data.images?.[0] || undefined,
    tags: data.tags || [],
    category: data.category || undefined,
    displayOrder: normalizeDisplayOrder(data.displayOrder),
    purchaseTypes: {
      oneTime: data.purchaseTypes?.oneTime ?? false,
      subscription: data.purchaseTypes?.subscription ?? false
    },
    prices: normalizePrices(data.prices),
    items: normalizeItems(data.items),
    shippingConfig: data.shippingConfig || {},
    addressPolicy: data.addressPolicy || 'fixed',
    defaultAddress: data.defaultAddress || null,
    published: data.published ?? false,
    disabled: data.disabled ?? false,
    activeSubscriptionCount: data.activeSubscriptionCount || 0,
    stripe: data.stripe || undefined,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt)
  };
};

const sortBundlesByDisplayOrder = (bundles: Bundle[]): Bundle[] => {
  const total = bundles.length;
  const entries = bundles.map((bundle, index) => ({
    bundle,
    index,
    order: normalizeDisplayOrder(bundle.displayOrder)
  }));

  const inRange = entries.filter((entry) => entry.order != null && entry.order <= total);
  const overflow = entries.filter((entry) => entry.order != null && entry.order > total);
  const defaults = entries.filter((entry) => entry.order == null);

  const sortByOrderThenIndex = (a: typeof entries[number], b: typeof entries[number]) => {
    if ((a.order ?? 0) !== (b.order ?? 0)) {
      return (a.order ?? 0) - (b.order ?? 0);
    }
    return a.index - b.index;
  };

  inRange.sort(sortByOrderThenIndex);
  overflow.sort(sortByOrderThenIndex);
  defaults.sort((a, b) => a.index - b.index);

  return [...inRange, ...defaults, ...overflow].map((entry) => entry.bundle);
};

export const fetchPublishedBundles = async (): Promise<Bundle[]> => {
  const bundlesRef = collection(db, BUNDLES_COLLECTION);
  const bundleQuery = query(
    bundlesRef,
    where('published', '==', true),
    where('disabled', '!=', true),
    orderBy('disabled'),
    orderBy('createdAt', 'desc'),
    limit(60)
  );

  const snapshot = await getDocs(bundleQuery);
  const mapped = snapshot.docs.map((docSnap) => mapBundleDoc(docSnap));
  return sortBundlesByDisplayOrder(mapped);
};

export const fetchBundleBySlug = async (slug: string): Promise<Bundle | null> => {
  const bundlesRef = collection(db, BUNDLES_COLLECTION);
  const bundleQuery = query(
    bundlesRef,
    where('slug', '==', slug),
    limit(1)
  );

  const snapshot = await getDocs(bundleQuery);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return mapBundleDoc(docSnap);
};

export const fetchBundleById = async (bundleId: string): Promise<Bundle | null> => {
  const bundleRef = doc(db, BUNDLES_COLLECTION, bundleId);
  const bundleSnap = await getDoc(bundleRef);
  if (!bundleSnap.exists()) return null;
  return mapBundleDoc(bundleSnap);
};

export const getBundleSubscriptionForUser = async (
  bundleId: string,
  userId: string
): Promise<BundleSubscription | null> => {
  if (!userId) return null;
  const subscriptionRef = doc(db, BUNDLE_SUBSCRIPTIONS_COLLECTION, `${bundleId}_${userId}`);
  const subscriptionSnap = await getDoc(subscriptionRef);
  if (!subscriptionSnap.exists()) return null;

  const data = subscriptionSnap.data();
  return {
    id: subscriptionSnap.id,
    bundleId: data.bundleId,
    userId: data.userId,
    status: data.status,
    stripeSubscriptionId: data.stripeSubscriptionId ?? null,
    stripeScheduleId: data.stripeScheduleId ?? null,
    stripeCustomerId: data.stripeCustomerId,
    lastOrderCreatedAt: convertTimestamp(data.lastOrderCreatedAt) || null,
    currentPeriodEnd: convertTimestamp(data.currentPeriodEnd) || null,
    shippingTierId: data.shippingTierId || null,
    addressPolicy: data.addressPolicy || 'fixed',
    nextBillingAt: convertTimestamp(data.nextBillingAt) || null
  };
};

interface SubscriptionCheckoutPayload {
  bundleId: string;
  currency: SupportedCurrency;
  userId: string;
  userEmail: string;
  successUrl: string;
  cancelUrl: string;
  shippingAddress: {
    name: string;
    phone?: string;
    email?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
  shippingTierId?: string | null;
}

interface OneTimePaymentIntentPayload {
  bundleId: string;
  currency: SupportedCurrency;
  userId: string;
  userEmail?: string;
  shippingAddress: SubscriptionCheckoutPayload['shippingAddress'];
  shippingTierId?: string | null;
}

export const createBundleSubscriptionCheckoutSession = async (
  payload: SubscriptionCheckoutPayload
): Promise<{ url: string; sessionId?: string; mode: 'subscription' | 'setup' }> => {
  const response = await fetch(`${API_BASE_URL}/bundles/subscription/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.error || 'Failed to create subscription checkout session');
  }

  const data = await response.json();
  return {
    url: data.url,
    sessionId: data.sessionId,
    mode: data.mode || 'subscription'
  };
};

export const createBundleSubscriptionPortalSession = async (
  args: { bundleId: string; userId: string; returnUrl: string }
): Promise<{ url: string }> => {
  const response = await fetch(`${API_BASE_URL}/bundles/subscription/create-portal-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args)
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.error || 'Failed to create billing portal session');
  }
  return response.json();
};

export const createBundleOneTimePaymentIntent = async (
  payload: OneTimePaymentIntentPayload
): Promise<{ clientSecret: string; orderId: string; amount: number; total: number; currency: string; paymentIntentId: string }> => {
  const response = await fetch(`${API_BASE_URL}/bundles/one-time/create-payment-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.error || 'Failed to create payment intent');
  }

  return response.json();
};

export const buildAddressSnapshot = (details: {
  name: string;
  phone?: string;
  email?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}) => ({
  name: details.name,
  phone: details.phone,
  email: details.email,
  address: {
    line1: details.address.line1,
    line2: details.address.line2 || '',
    city: details.address.city,
    state: details.address.state,
    postal_code: details.address.postal_code,
    country: details.address.country
  }
});

export const buildSubscriptionCheckoutPayload = (
  args: SubscriptionCheckoutPayload
): SubscriptionCheckoutPayload => ({
  ...args,
  shippingAddress: buildAddressSnapshot(args.shippingAddress)
});

export const buildOneTimeCheckoutPayload = (
  args: OneTimePaymentIntentPayload
): OneTimePaymentIntentPayload => ({
  ...args,
  shippingAddress: buildAddressSnapshot(args.shippingAddress)
});

export const getBundleReferralCode = (): string | undefined => getReferralCookie();
